import Input from "../General/Input";
import Button from "../General/Button";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import FixedPriceSchema from "../../validations/FixedPrice";
import Axios from "../../utils/axios";
import helpers from "../../utils/helpers";
import urls from "../../constants/api-endpoints";
import { useState, useRef, useEffect } from "react";
import { setSnackBar } from "../../store/ToastSlice";
import accessToken from "../../utils/access-token";
import { useFormik } from "formik";
import { getCertificateList, getPharmacopoeiaGrades, getCountryList, getAPIList } from "../../store/CoreSlice";
import { MixpanelTracking } from "../../MixPanel";
import Select from "../General/Select";
import EnquiryResponseSuccess from "../EnquiryResponse/Success";
import { handleAuthModal } from "../../store/AuthSlice";
import { MixPanelConstants } from "../../constants/MixPanelEvents";

const CirciledDiv = styled.div`
    background-color : rgba(255,255,255, 0.2)
`

function ProductListing() {
    const initialValues: any = {
        "seller_product": {
            "product": null,
            "pharmacopoeia_grades": [],
            "certificates": [],
            "country" : null
        },
        "product_search_string": '',
        "country_search_string" : '',
        "price": null,
        "quantity_available": null,
        "minimum_order_quantity": null,
        "validity": null,
        "quantity_available_unit": 1,
        "minimum_order_quantity_unit": 1,
        "payment_terms": ''
    }

    const dispatch: any = useDispatch(); 
    const { apiList: { list }, certificateList, unitList, pharmacopoeiaGradesList, countryList } = useSelector((state: any) => state.core);
    const { user } = useSelector((state : any) => state.auth);
	let [countryString, setCountryString] = useState('');
    const [productString, setProductString] = useState('');
    const [showProductResult, setShowProductResult] = useState(() => false);
	const [showCountryResults, setShowCountryResults]: any = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [payload, setPayload] = useState({})

    const { values, errors, handleSubmit, handleChange, touched, handleBlur, resetForm, setFieldValue } = useFormik({
        initialValues: initialValues,
        validationSchema: FixedPriceSchema,
        onSubmit: (values, action) => {
            delete values['product_search_string'];
            delete values['country_search_string'];

            if(user.isLoggedIn)
                submitForm(values);
            else {
                setPayload((previousValue) => {
                    return {...previousValue, ...values}
                })
                dispatch(handleAuthModal(true));
            }
        }
    });

    useEffect(() => {
        dispatch(getCertificateList());
        dispatch(getPharmacopoeiaGrades());
        MixpanelTracking.getInstance().pageViewed({});
    }, []);

    useEffect(() => {
        if(user.isLoggedIn && Object.keys(payload).length !== 0)
            submitForm(payload);
    }, [user.isLoggedIn])

    // FUNCTIONS
    const handleProductSearch = (event: any) => {
        setShowProductResult(() => true);
        setProductString(() => event.target.value);
        const params = {
            search: event.target.value,
            page: 1,
            page_size: 5
        }
        dispatch(getAPIList(params));
    }

    const handleCountrySearch = (event: any) => {
        setShowCountryResults(() => true);
        setCountryString(() => event.target.value);
        const params = {
            search: event.target.value,
            page: 1,
            page_size: 5
        }
        dispatch(getCountryList(params));
    }

    const selectProduct = (item: any) => {
        setProductString(() => item.name);
        setFieldValue('seller_product.product', item.id);
        setShowProductResult(() => false);
    }

    const selectCountry = (item: any) => {
		setCountryString(() => item.name);
        setFieldValue('seller_product.country', item.id);
		setShowCountryResults(() => false);
	}

    const handleProductClear = () => {
        setProductString('');
        setFieldValue('seller_product.product', null);
        setFieldValue('product_search_string', '');
    }

    const handleCountryClear = () => {
		setCountryString('');
        setFieldValue('country_search_string', '');
	}

    const submitForm = (payload: object) => {       

        const data : any = {...payload}

        // Converting to array of ids
        data['seller_product']['certificates'] = data['seller_product']['certificates'].map((certificate : any) => certificate.id);
        data['seller_product']['pharmacopoeia_grades'] = data['seller_product']['pharmacopoeia_grades'].map((grade : any) => grade.id);

        const mixPanelData : any = {
            form : 'List Your Product',
            userInput : data
        }

        const endPoint = helpers.getAPIEndpoint(urls.fixedPrice);

        const headers = {
            Authorization: `Bearer ${accessToken.getAccessToken()}`
        }

        const successHandler = (response: any) => {
            if (response.status === 201) {
                const emptyObject = {}
                setShowSuccessModal(true);
                setProductString('');
                setCountryString('');
                resetForm();
                setPayload(emptyObject);

                mixPanelData['status'] = MixPanelConstants.SUCCESS;
                MixpanelTracking.getInstance().formSubmitted(mixPanelData)
            }
        }

        const errorHandler = (error: any) => {
            console.log(error);
            const message = error.response.data.detail || 'Something went wrong internally!'
            dispatch(setSnackBar(
                {
                    color: 'error',
                    message: message
                }
            ));

            mixPanelData['status'] = MixPanelConstants.FAILED
            mixPanelData['reason'] = message
            MixpanelTracking.getInstance().formSubmitted(mixPanelData)
        }

        Axios.requestPOST(
            endPoint,
            data,
            headers,
            successHandler,
            errorHandler
        )
    }    

    return (
        <div className="bg-[#324D9F] --align">
            <div className="py-10 lg:flex lg:justify-between lg:py-[100px]">
                <h1 className="text-white text-2xl py-7 lg:text-6xl lg:w-[30%] capitalize relative">
                    List your first product. It’s free!
                    <CirciledDiv className="hidden lg:block w-[120px] h-[120px] rounded-[50%] absolute bottom-[170px] left-[50px]" />
                </h1>

                <form onSubmit={handleSubmit} className="my-2 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3 lg:w-[65%] lg:gap-y-10">

                    <div className="lg:col-span-2 relative">
                        <Input
                            placeholder="Select Product"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 rounded py-2 px-2"
                            properties="placeholder:text-white"
                            name='product_search_string'
                            value={productString}
                            onBlur={handleBlur}
                            error={errors.product_search_string}
                            touched={touched.product_search_string}
                            onChange={handleChange}
                            onInput={handleProductSearch}
                            suffix={
                                productString ?
                                    <div onClick={handleProductClear} className='cursor-pointer text-sm flex justify-center items-center'>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-white">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </div>
                                    :
                                    <div>
                                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-white">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                        </svg>
                                    </div>
                            }
                            search
                            searchResults={
                                showProductResult &&
                                list.map((data: any, index: number) => (
                                    <div className="
                                        flex 
                                        justify-between 
                                        text-[12px] 
                                        my-1 
                                        py-2 
                                        text-theme-black 
                                        font-light 
                                        cursor-pointer 
                                        rounded 
                                        items-center 
                                        hover:bg-[#D4D9E7]/50 
                                        font-semibold"
                                        onClick={() => selectProduct(data)}
                                        key={index}
                                    >
                                        <p className="flex flex-col font-light">
                                            <span>
                                                {data.name} ({data.cas_number})
                                            </span>
                                            <small> {data.product_type} </small>
                                        </p>
                                    </div>
                                ))
                            }
                        />
                    </div>
                    <div className="relative">
                        <Input
                            placeholder="Country of Manufacturing"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 border-[0.5px] rounded py-2 px-2"
                            properties="placeholder:text-white"
                            value={countryString}
                            search
							error={errors.country}
							touched={errors.country}
                            onChange={handleChange}
                            onInput={handleCountrySearch}
							onBlur = {() => {
								setTimeout(() => {
									setShowCountryResults(false);
								}, 300);
							}}
							searchResults={
								showCountryResults &&
								countryList.list.map((data: any, index: number) => (
									<div className="
												flex 
												justify-between 
												text-[12px] 
												my-1 
												py-2 
												text-theme-black 
												font-light 
												cursor-pointer 
												rounded 
												items-center 
												hover:bg-[#D4D9E7]/50 
												font-semibold"
										onClick={() => selectCountry(data)}
										key={index}
									>
										<p className="flex flex-col font-light">
											<span>
												{data.name}
											</span>
										</p>
									</div>
								))
							}
							suffix={
								countryString ?
								<div onClick={handleCountryClear} className='cursor-pointer text-sm flex justify-center items-center'>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-white">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </div>
                                :
                                <div>
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4 text-white">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                                    </svg>
                                </div>
							}
                        />
                    </div>

                    <div>
                        <Select
                            placeholder="Quality (Multi-select)"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 border-[0.5px] rounded py-2 px-2"
                            additionalStyles={{
                                padding: '2px 0'
                            }}
                            multi
                            items={certificateList.list}
                            onChange={(selected : any) => setFieldValue('seller_product.certificates', selected)}
                            value = {values.seller_product.certificates}
                            placeholderStyles={{
                                fontSize: 13,
                                fontWeight: 300,
                                color : 'white'
                            }}
                        />
                    </div>
                    <div>
                        <Select
                            placeholder="Pharmacopeia (Single-select)"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 border-[0.5px] rounded py-2 px-2"
                            properties="placeholder:text-white"
                            additionalStyles={{
                                padding: '2px 0'
                            }}
                            items={pharmacopoeiaGradesList.list}
                            onChange={(selected : any) => setFieldValue('seller_product.pharmacopoeia_grades', [selected])}
                            value = {values.seller_product.pharmacopoeia_grades}
                            placeholderStyles={{
                                fontSize: 13,
                                fontWeight: 300,
                                color : 'white'
                            }}
                            valueColor = 'white'
                        />
                    </div>
                    <div>
                        <Input
                            placeholder="Qty available"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 border-[0.5px] rounded py-2 px-2"
                            properties="placeholder:text-white"
                            name='quantity_available'
                            value={values.quantity_available}
                            onBlur={handleBlur}
                            error={errors.quantity_available}
                            touched={touched.quantity_available}
                            onChange={handleChange}
                            suffix = {
                                <p className="text-white"> KG </p>
                            }
                        />
                    </div>

                    <div>
                        <Input
                            placeholder="Price per KG"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 border-[0.5px] rounded py-2 px-2"
                            properties="placeholder:text-white"
                            name='price'
                            value={values.price}
                            onBlur={handleBlur}
                            error={errors.price}
                            touched={touched.price}
                            onChange={handleChange}
                        />
                    </div>

                    <div>
                        <Input
                            placeholder="MOQ"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 border-[0.5px] rounded py-2 px-2"
                            properties="placeholder:text-white"
                            name='minimum_order_quantity'
                            value={values.minimum_order_quantity}
                            onBlur={handleBlur}
                            error={errors.minimum_order_quantity}
                            touched={touched.minimum_order_quantity}
                            onChange={handleChange}
                            suffix = {
                                <p className="text-white"> KG </p>
                            }
                        />
                    </div>

                    <div className="md:col-span-2">
                        <Input
                            placeholder="Payment terms valid till"
                            background="transparent"
                            color='white'
                            outerProperties="border border-slate-400 border-[0.5px] rounded py-2 px-2"
                            properties="placeholder:text-white"
                            name='payment_terms'
                            value={values.payment_terms}
                            onBlur={handleBlur}
                            error={errors.payment_terms}
                            touched={touched.payment_terms}
                            onChange={handleChange}
                        />
                    </div>

                    <div className="lg:col-span-3 mt-4">
                        <Button styles='bg-theme-green text-white px-6 h-[45px] w-full md:w-[200px] lg:py-7' borderRadius={39} destinationEvent="">
                            Submit
                        </Button>
                    </div>

                </form>
            </div>

            <EnquiryResponseSuccess 
                show = {showSuccessModal}
                onClose = {() => setShowSuccessModal(false)}
            />
        </div>
    )
}

export default ProductListing